/**
 * This service is responsible for speech to text generation by sending the audio file to the backend.
 * The backend will send the audio file to openAI Whisper API and return the generated text.
 */

import { Injectable } from '@angular/core';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import type { ExtraData } from './speech-to-text.types';
import { ACCEPTED_FILE_TYPES } from './speech-to-text.types';

@UntilDestroy({ className: 'SpeechToText' })
@Injectable({
  providedIn: 'root',
})
export class SpeechToText {
  constructor(private freelancerHttp: FreelancerHttp) {}

  async transcribeSpeech(audioFile: File, extras?: ExtraData): Promise<string> {
    if (!ACCEPTED_FILE_TYPES.includes(audioFile.type)) {
      throw new Error('File type not supported');
    }

    const formData = new FormData();
    formData.append('audioFile', audioFile);
    formData.append('extras', JSON.stringify(extras));

    return firstValueFrom(
      this.freelancerHttp
        .post('speech-to-text/generateTranscription.php', formData, {
          isGaf: true,
        })
        .pipe(untilDestroyed(this)),
    ).then(response => {
      if (response.status === 'success') {
        return response.result.generatedText;
      }

      throw new Error('Unsuccessful request');
    });
  }

  async transcribeAndTranslateSpeech(
    audioFile: File,
    extras?: ExtraData,
  ): Promise<string> {
    if (!ACCEPTED_FILE_TYPES.includes(audioFile.type)) {
      throw new Error('File type not supported');
    }

    const formData = new FormData();
    formData.append('audioFile', audioFile);
    formData.append('extras', JSON.stringify(extras));

    return firstValueFrom(
      this.freelancerHttp
        .post('speech-to-text/generateTranslation.php', formData, {
          isGaf: true,
        })
        .pipe(untilDestroyed(this)),
    ).then(response => {
      if (response.status === 'success') {
        return response.result.generatedText;
      }

      throw new Error('Unsuccessful request');
    });
  }
}
